import React, { useState, useRef } from "react";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

interface OTPInputProps {
  onComplete: (otp: string) => Promise<void>;
}

// Faint ink focus ring — same literal as theme/index.ts controlOutline (no token).
const FOCUS_RING = "rgba(15, 15, 14, 0.08)";
// Resting border for form controls — matches formControlBorder in theme/index.ts.
const INPUT_BORDER = "rgba(15, 15, 14, 0.18)";

/**
 * 6-box OTP input (2026-06-12-admin-login-prototype .otp-boxes): rounded
 * squares on the soft surface; a filled or focused box flips to the card
 * surface with an ink border, the focused one adding the faint ink ring.
 * Completion / focus-advance / backspace logic is unchanged.
 */
const OTPInput: React.FC<OTPInputProps> = ({ onComplete }) => {
  const length = 6;
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const [errorMsg, setErrorMsg] = useState("");
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = async (index: number, value: string) => {
    if (submitting) return;
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setErrorMsg("");

      // Move focus to next input if available
      if (value && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Check if OTP is complete
      if (newOtp.every((char) => char !== "")) {
        setSubmitting(true);
        try {
          await onComplete(newOtp.join(""));
        } catch (err) {
          setErrorMsg(translate("messages.error.incorrect_otp"));
        } finally {
          setSubmitting(false);
        }
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Move focus to previous input on backspace if empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
        {otp.map((digit, index) => {
          const focused = activeIdx === index;
          const filled = digit !== "";
          return (
            <input
              key={index}
              value={digit}
              maxLength={1}
              inputMode="numeric"
              aria-label={`OTP digit ${index + 1}`}
              disabled={submitting}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onFocus={() => setActiveIdx(index)}
              onBlur={() => setActiveIdx(null)}
              ref={(el) => (inputRefs.current[index] = el)}
              style={{
                flex: 1,
                width: "100%",
                maxWidth: 58,
                aspectRatio: "1",
                borderRadius: 14,
                textAlign: "center",
                fontFamily: "var(--font-display)",
                fontSize: textPx(24),
                fontWeight: 600,
                color: colors.ink,
                background: filled || focused ? colors.card : colors.bgSoft,
                border: `1px solid ${
                  errorMsg ? colors.error : filled || focused ? colors.ink : INPUT_BORDER
                }`,
                boxShadow: focused ? `0 0 0 3px ${FOCUS_RING}` : "none",
                outline: "none",
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? "progress" : "text",
                transition: "border-color 150ms, box-shadow 150ms, background 150ms",
              }}
            />
          );
        })}
      </div>
      {errorMsg && (
        <div style={{ color: colors.error, fontSize: textPx(12.5), marginTop: 10 }}>{errorMsg}</div>
      )}
    </div>
  );
};

export default OTPInput;
